import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as moment from 'moment';
import { catchError, debounceTime, EMPTY, Subject, switchMap } from 'rxjs';
import { SharedDataService } from 'src/app/services/app/shared-service/shared-data.service';
import { SupportService } from 'src/app/services/core/support/support.service';
import { NUMBER_OF_RECORDS } from 'src/app/utils/common.utils';
import { sweetAlert } from 'src/app/utils/swal';
import { timeZone } from 'src/app/utils/utils.common';
import {
  applyTicketUpdate,
  prepareTicket,
  SUPPORT_ROLE_LABELS,
  SUPPORT_SCOPE_BADGES,
  SUPPORT_TRIP_SOURCES,
  TICKET_STATUS_OPTIONS,
  trackById
} from 'src/app/utils/support.utils';
@UntilDestroy()

@Component({
  selector: 'app-support-tickets',
  templateUrl: './support-tickets.component.html',
  styleUrls: ['./support-tickets.component.css']
})
export class SupportTicketsComponent implements OnInit {
  tickets: any = [];
  totalResults = 0;
  totalPages = 0;
  currentPage = 1;
  numberOfRecords = NUMBER_OF_RECORDS;
  search: any;
  selectedStatus = '';
  tripId: any;
  tripSource: any;
  selectedReference: any;
  isAssociationInfoOpen = false;
  currentUser: any;
  timeZone: string = timeZone;
  sortBy = '';
  sortOrder: 'asc' | 'desc' = 'asc';
  dateRange: any = this.getTodayRange();
  statusCounts: any = TICKET_STATUS_OPTIONS.reduce((counts: any, status) => ({ ...counts, [status.value]: 0 }), { total: 0 });
  statusOptions = TICKET_STATUS_OPTIONS;
  roleLabels = SUPPORT_ROLE_LABELS;
  scopeBadges = SUPPORT_SCOPE_BADGES;
  trackById = trackById;
  private readonly searchSubject = new Subject<string>();
  private readonly ticketsRequest = new Subject<number>();

  constructor(
    private supportService: SupportService,
    private sharedDataService: SharedDataService,
    private router: Router
  ) { }

  ngOnInit() {
    this.getUser();
    this.listenTicketsRequests();
    this.getTickets(this.currentPage);
    this.listenSearch();
    this.listenTicketEvents();
  }

  getUser() {
    this.sharedDataService.getUser().pipe(untilDestroyed(this)).subscribe((user) => {
      this.currentUser = user;
      // today in the user's time zone, the list was first loaded for the default one
      if (user?.timeZone && user.timeZone !== this.timeZone) {
        this.timeZone = user.timeZone;
        this.dateRange = this.getTodayRange();
        this.getTickets(this.currentPage = 1);
      }
    });
  }

  getTickets(page: number) {
    this.ticketsRequest.next(page);
  }

  listenTicketsRequests() {
    this.ticketsRequest.pipe(
      switchMap((page) => this.supportService.getTickets(this.getQueryParams(page)).pipe(
        catchError((err) => {
          sweetAlert('Error', err, 'error', 'OK');
          return EMPTY;
        })
      )),
      untilDestroyed(this)
    ).subscribe((data: any) => {
      if (data && data.results) {
        this.tickets = data.results.map(prepareTicket);
        this.totalPages = data.totalPages;
        this.totalResults = data.totalResults;
        if (data.statusCounts) {
          this.statusCounts = { ...this.statusCounts, ...data.statusCounts };
        }
      }
    });
  }

  getQueryParams(page: number) {
    const queryParams: any = {
      limit: this.numberOfRecords,
      page,
      sortBy: this.sortBy,
      sortOrder: this.sortOrder,
    };
    // cleared range lists the tickets of every date
    if (this.dateRange && this.dateRange[0] && this.dateRange[1]) {
      queryParams.time = [
        moment(this.dateRange[0]).startOf('day').toISOString(),
        moment(this.dateRange[1]).endOf('day').toISOString(),
      ];
    }
    if (this.selectedStatus) {
      queryParams.status = this.selectedStatus;
    }
    if (this.search) {
      queryParams.search = this.search;
    }
    return queryParams;
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.getTickets(page);
  }

  onSelectStatus(event: any) {
    this.selectedStatus = event.target.value;
    this.getTickets(this.currentPage = 1);
  }

  getTodayRange() {
    return [moment().tz(this.timeZone).startOf('day'), moment().tz(this.timeZone).endOf('day')];
  }

  onSort(field: string) {
    this.sortOrder = this.sortBy === field && this.sortOrder === 'asc' ? 'desc' : 'asc';
    this.sortBy = field;
    this.getTickets(this.currentPage = 1);
  }

  onDateChange(range: any) {
    this.dateRange = range && range[0] && range[1] ? [range[0], moment(range[1]).endOf('day')] : null;
    this.getTickets(this.currentPage = 1);
  }

  onSearch() {
    this.searchSubject.next(this.search);
  }

  listenSearch() {
    this.searchSubject.pipe(debounceTime(500), untilDestroyed(this)).subscribe(() => {
      this.getTickets(this.currentPage = 1);
    });
  }

  listenTicketEvents() {
    // new tickets come first, so only the first page changes
    this.supportService.ticketCreated$.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.currentPage === 1) {
        this.getTickets(1);
      }
    });
    this.supportService.ticketUpdated$.pipe(untilDestroyed(this)).subscribe((data: any) => this.onTicketUpdated(data));

    this.supportService.chatMessage$.pipe(untilDestroyed(this)).subscribe((data: any) => {
      const ticket = this.findTicketByChat(data.chatId);
      if (ticket && data.message.sender?.id !== this.currentUser?.id) {
        ticket.unreadCount += 1;
      }
    });
    this.supportService.chatRead$.pipe(untilDestroyed(this)).subscribe((data: any) => {
      const ticket = this.findTicketByChat(data.chatId);
      if (ticket && data.userId === this.currentUser?.id) {
        ticket.unreadCount = 0;
      }
    });
  }

  findTicketByChat(chatId: string) {
    return this.tickets.find((ticket: any) => ticket.chat === chatId);
  }

  onTicketUpdated(data: any) {
    const ticket = this.tickets.find((loadedTicket: any) => loadedTicket.id === data.ticketId);
    if (!ticket) {
      return;
    }
    // it no longer belongs to the filtered status
    if (this.selectedStatus && data.status !== this.selectedStatus) {
      this.getTickets(this.currentPage);
      return;
    }
    applyTicketUpdate(ticket, data);
  }

  openTicket(ticket: any) {
    this.router.navigate(['/support/tickets', ticket.id]);
  }

  // a trip or a bill opens the trip details, anything else the reference details
  openReference(event: Event, ticket: any) {
    event.stopPropagation();
    this.tripSource = SUPPORT_TRIP_SOURCES[ticket.reference.type];
    if (this.tripSource) {
      this.tripId = ticket.reference.id;
    } else {
      this.selectedReference = ticket.reference;
    }
  }
}
